// Pure scoring reducer for a Game Mode round. Each tap on a media tile is a
// "hit": base points plus a combo bonus for fast consecutive hits. The
// daily-return multiplier is applied separately (server-authoritative) — this
// computes the raw, pre-multiplier score the client shows live.
import type { SloganKind } from '../types'

export const BASE_HIT = 100
export const COMBO_BONUS = 25
export const COMBO_WINDOW_MS = 1500
/** Mirror of the worker's MAX_POINTS_PER_HIT clamp. */
export const MAX_HIT_POINTS = 1000
/** Combo counts that fire a milestone splash. */
export const MILESTONES = [5, 10, 20, 30, 50]

export interface RoundState {
  score: number
  hits: number
  combo: number
  bestCombo: number
  lastHitAt: number
}

export function initRound(): RoundState {
  return { score: 0, hits: 0, combo: 0, bestCombo: 0, lastHitAt: 0 }
}

export interface HitOutcome {
  next: RoundState
  gained: number
  combo: number
  comboBroke: boolean
  milestone: number | null
  kind: SloganKind
}

/** Register a hit at `now`. Returns the new state + what to celebrate. */
export function registerHit(s: RoundState, now: number): HitOutcome {
  const inWindow = s.lastHitAt > 0 && now - s.lastHitAt <= COMBO_WINDOW_MS
  const combo = inWindow ? s.combo + 1 : 1
  const comboBroke = !inWindow && s.combo > 1
  const gained = Math.min(MAX_HIT_POINTS, BASE_HIT + COMBO_BONUS * (combo - 1))
  const milestone = MILESTONES.includes(combo) ? combo : null
  const next: RoundState = {
    score: s.score + gained,
    hits: s.hits + 1,
    combo,
    bestCombo: Math.max(s.bestCombo, combo),
    lastHitAt: now,
  }
  const kind: SloganKind = milestone
    ? 'milestone'
    : combo >= 5
      ? 'combo'
      : combo >= 3
        ? 'bonus'
        : 'hit'
  return { next, gained, combo, comboBroke, milestone, kind }
}

/** Project the final score the player will bank, given today's multiplier. */
export function projectedFinal(rawScore: number, multiplier: number): number {
  return Math.round(rawScore * multiplier)
}
